import React, { useEffect, useState } from "react";

import { Grid } from "@material-ui/core";
import { useFormikContext } from "formik";
import { useSelector } from "react-redux";

import SlimButton from "components/Button/SlimButton";
import { standardFilter } from "components/Form/FormikControls";
import { ConsignmentAutocomplete } from "components/SaleLotForms/ConsignmentAutocomplete";

import { ConsignmentPermission } from "constants/permissions";

import { openEditConsignmentModal } from "lib/navigation";
import { hasPermission } from "lib/permissions";

import {
  getConsignments,
  selectCurrentDeploymentSales,
  selectExportSiteOptionsByConsignmentIdLookup,
  selectExportSitesByDeploymentSaleIdLookup,
  selectUnknownBusinessIds,
} from "selectors";

import { useHasAddConsignmentPermission } from "hooks/useHasPermission";

const InlineConsignmentSelect = ({
  name = "consignment_id",
  label = "Consignment",
  autoFocus = true,
  focusNextInput = undefined,
  includeCreateConsignmentButton = false,
  includeCreateUnknownConsignment = false,
  ignoreHeadCountForVendorSearch = false,
  onChangeExtra = undefined,
  showConsignmentCode = true,
  showAgencyName = true,
}) => {
  const formikProps = useFormikContext();
  const { values } = formikProps;
  const consignmentsById = useSelector(getConsignments);
  const deploymentSales = useSelector(selectCurrentDeploymentSales);
  const unknownBusinessIds = useSelector(selectUnknownBusinessIds);
  const exportSitesByConsignmentIdLookup = useSelector(
    selectExportSiteOptionsByConsignmentIdLookup,
  );
  const exportSitesByDeploymentSaleIdLookup = useSelector(
    selectExportSitesByDeploymentSaleIdLookup,
  );

  const onAddConsignment = () => {
    openEditConsignmentModal();
  };

  // The first time we add the unknown business consignment, we use the same machinery,
  // but infer what the value refers to by checking its type.  If its a string/uuid, its
  // consignment, if its a number, its a livestock agency id.
  const isConsignmentId = value => value && typeof value === "string";
  const isLivestockAgencyId = value => value && typeof value === "number";

  const hasAddConsignmentPermission = useHasAddConsignmentPermission();

  // Deferred to an effect so it runs after ConsignmentAutocomplete has
  // finished switching from the select to the selected-value card - focusing
  // synchronously during handleChange can otherwise be undone by that
  // transition unmounting the select's input.
  const [justSelected, setJustSelected] = useState(false);
  useEffect(() => {
    if (justSelected) {
      focusNextInput?.();
      setJustSelected(false);
    }
  }, [justSelected, focusNextInput]);

  let consignmentOptionFilter;
  if (ignoreHeadCountForVendorSearch) {
    consignmentOptionFilter = (option, input) => {
      const labelWithoutHeadCount = option.label
        .toLowerCase()
        .replace(/\(hd \d+\)/, "");
      return labelWithoutHeadCount.includes(input.toLowerCase());
    };
  }

  const setExportSites = value => {
    let exportSites = [];
    if (isConsignmentId(value)) {
      exportSites =
        exportSitesByConsignmentIdLookup[value]?.map(o => o.value) || [];
    } else if (isLivestockAgencyId(value)) {
      const deploymentSaleId = Object.values(deploymentSales).find(
        ds => ds.livestock_agency_id === value,
      ).deployment_sale_id;
      exportSites = exportSitesByDeploymentSaleIdLookup[deploymentSaleId] || [];
    }

    formikProps.setFieldValue("exportSites", exportSites);
  };

  const handleChange = value => {
    setExportSites(value);
    if (value) {
      setJustSelected(true);
    }
    if (onChangeExtra) {
      onChangeExtra(value);
    }
  };

  const filterOptions = consignmentOptionFilter
    ? (options, params) =>
        options.filter(option =>
          consignmentOptionFilter(option, params.inputValue),
        )
    : standardFilter;
  const hasSelectableConsignments = Object.values(consignmentsById).some(
    consignment =>
      hasPermission(consignment, ConsignmentPermission.canAddSaleLot) ||
      values[name] === consignment.id,
  );
  const deploymentSaleIdsWithUnknownConsignments = Object.values(
    consignmentsById,
  )
    .filter(consignment => unknownBusinessIds.includes(consignment.vendor_id))
    .reduce((acc, consignment) => {
      acc.add(consignment.deployment_sale);
      return acc;
    }, new Set());
  const hasCreateUnknownConsignmentOptions =
    includeCreateUnknownConsignment &&
    Object.values(deploymentSales).some(
      deploymentSale =>
        !deploymentSaleIdsWithUnknownConsignments.has(
          deploymentSale.deployment_sale_id,
        ),
    );

  return (
    <>
      {includeCreateConsignmentButton &&
      !hasSelectableConsignments &&
      !hasCreateUnknownConsignmentOptions &&
      hasAddConsignmentPermission ? (
        <Grid container item xs={12} justifyContent="center">
          <SlimButton onClick={onAddConsignment} type="button" fullWidth>
            Create A New Consignment
          </SlimButton>
        </Grid>
      ) : (
        <Grid item xs={12}>
          <ConsignmentAutocomplete
            required
            name={name}
            label={label}
            autoFocus={autoFocus}
            onChangeExtra={handleChange}
            filterOptions={filterOptions}
            includeCreateUnknownConsignmentOptions={
              includeCreateUnknownConsignment
            }
            limitToCanAddSaleLotPermission
            optionLabelMode="inline"
            showSelectedAsCard
            showAgencyName={showAgencyName}
            showConsignmentCode={showConsignmentCode}
            sortKnownConsignmentsFirst
            selectedEditButtonDataTour="editConsignment"
          />
        </Grid>
      )}
    </>
  );
};

export default InlineConsignmentSelect;
